export interface ColumnSnippet {
  /** The SQL expression that produces the column (e.g. "COALESCE(a, b) AS col") */
  expression: string
  /** The FROM clause showing which table(s) are sourced, if found */
  from: string | null
}

/**
 * Extract the SQL expression that produces a given column alias from compiled SQL.
 * Walks backwards from the alias match to find the top-level comma or SELECT
 * keyword that starts the expression, skipping commas inside parentheses.
 */
export function extractColumnSnippet(
  compiledSql: string,
  columnName: string
): ColumnSnippet | null {
  // Match "AS column_name" (with optional quoting), followed by comma / whitespace / end
  const escaped = columnName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const aliasPattern = new RegExp(
    `\\bas\\s+(?:"|'|\`)?${escaped}(?:"|'|\`)?\\s*(?:,|\\s|$)`,
    'gim'
  )
  const match = aliasPattern.exec(compiledSql)
  if (!match) return null

  // Walk backwards from the match to find the expression start.
  // Skip commas that are inside parentheses (e.g. function arguments).
  const before = compiledSql.slice(0, match.index)
  let depth = 0
  let exprStart = 0

  for (let i = before.length - 1; i >= 0; i--) {
    const ch = before[i]
    if (ch === ')') depth++
    else if (ch === '(') depth--
    else if (ch === ',' && depth === 0) {
      exprStart = i + 1
      break
    }
    // Check for SELECT keyword at current position
    if (depth === 0) {
      const slice = before.slice(Math.max(0, i - 6), i + 1)
      if (/\bselect\s*$/i.test(slice)) {
        exprStart = i - (slice.match(/\bselect\s*$/i)?.[0].length ?? 0) + 1
        break
      }
    }
  }

  // Trim the alias end — stop right after "AS column_name" (strip trailing comma)
  const aliasEnd = match.index + match[0].length
  const expression = compiledSql
    .slice(exprStart, aliasEnd)
    .trim()
    .replace(/,\s*$/, '')

  if (!expression) return null

  // Find the FROM clause that follows the SELECT list containing this column.
  // Search forward from the alias match for the next top-level FROM keyword.
  const after = compiledSql.slice(aliasEnd)
  let fromDepth = 0
  let fromStart = -1
  for (let i = 0; i < after.length; i++) {
    const ch = after[i]
    if (ch === '(') fromDepth++
    else if (ch === ')') fromDepth--
    else if (fromDepth === 0) {
      const rest = after.slice(i)
      if (/^\bfrom\b/i.test(rest)) {
        fromStart = i
        break
      }
    }
  }

  if (fromStart === -1) return { expression, from: null }

  // Capture "FROM ... " up to the next top-level keyword (WHERE, JOIN, GROUP, etc.) or end
  const fromText = after.slice(fromStart)
  const endPattern = /\b(?:where|join|inner|left|right|full|cross|on|group|having|order|limit|union|except|intersect)\b/i
  const endMatch = endPattern.exec(fromText.slice(4)) // skip the "FROM" word itself
  const fromClause = endMatch
    ? fromText.slice(0, 4 + endMatch.index).trim()
    : fromText.split('\n').slice(0, 3).join('\n').trim() // fallback: take a few lines

  return { expression, from: fromClause }
}
